import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { Styles, HAlignType } from "jspdf-autotable";
import { MOVEMENT_DATE_METRIC_OPTIONS, PDF_METRIC_KEYS } from "./constants";
import {
  formatCurrency,
  formatNumber,
  formatShortDayMonth,
  resolveUsuarioActual,
} from "./formatters";
import type {
  ControlStockRow,
  DateSelectionState,
  FamilyLineResultRow,
  MovementByDateRow,
  MovementDateMetric,
} from "./types";

type ExportTab =
  | "stock-por-articulo"
  | "movimientos-por-fecha"
  | "stock-por-familia-linea"
  | "all";

type ExcelExportConfig = {
  filteredData: ControlStockRow[];
  familyLineResults: FamilyLineResultRow[];
  movementDateAvailability: Record<MovementDateMetric, boolean>;
  movementDateColumns: string[];
  buildMovementRowsForMetric: (metric: MovementDateMetric) => MovementByDateRow[];
  metricMode: "cantidad" | "monto" | "ambos";
  activeStockTab:
    | "resumen"
    | "stock-por-articulo"
    | "movimientos-por-fecha"
    | "stock-por-familia-linea"
    | "stock-por-sucursal";
  hasAmountMode: boolean;
};

type PdfHiddenMetrics = Record<typeof PDF_METRIC_KEYS[number], boolean>;

type PdfExportConfig = {
  filteredData: ControlStockRow[];
  displayData: ControlStockRow[];
  familyLineResults: FamilyLineResultRow[];
  movementDateAvailability: Record<MovementDateMetric, boolean>;
  movementDateColumns: string[];
  buildMovementRowsForMetric: (metric: MovementDateMetric) => MovementByDateRow[];
  metricMode: "cantidad" | "monto" | "ambos";
  activeStockTab:
    | "resumen"
    | "stock-por-articulo"
    | "movimientos-por-fecha"
    | "stock-por-familia-linea"
    | "stock-por-sucursal";
  hasAmountMode: boolean;
  pdfHiddenMetrics: PdfHiddenMetrics;
  pdfHideCosto: boolean;
  logoDataUrl: string | null;
  serverFilter: string;
  branchFilter: string[];
  controlMode: "recetas" | "inventario";
  differenceFilter: "todas" | "con" | "sin" | "positivas" | "negativas";
  dateSelection: DateSelectionState;
  familyFilter: string;
  lineFilter: string;
  familyLabelMap: Record<string, string>;
  lineLabelMap: Record<string, string>;
  columnHasData: Record<string, boolean>;
  previousDateLabel: string;
  finalDateLabel: string;
  movementDateLabel: string;
};

export const exportControlStockExcel = (
  config: ExcelExportConfig,
  tab?: ExportTab,
) => {
  const {
    filteredData,
    familyLineResults,
    movementDateAvailability,
    movementDateColumns,
    buildMovementRowsForMetric,
    metricMode,
    activeStockTab,
    hasAmountMode,
  } = config;

  if (filteredData.length === 0) {
    return;
  }

  const selectedTab =
    tab ??
    (activeStockTab === "movimientos-por-fecha"
      ? "movimientos-por-fecha"
      : activeStockTab === "stock-por-familia-linea"
        ? "stock-por-familia-linea"
        : activeStockTab === "resumen"
          ? "stock-por-articulo"
        : "stock-por-articulo");
  const exportMode = metricMode === "ambos" ? "monto" : metricMode;
  const toMetricValue = (value: number, unitValue: number) =>
    exportMode === "cantidad" ? value : value * unitValue;

  const workbook = XLSX.utils.book_new();

  const addSheet = (name: string, rows: Array<Record<string, any>>) => {
    if (rows.length === 0) {
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, name);
  };

  const addArticuloSheet = () => {
    const rows = filteredData.map((row) => ({
      IDARTICULO: row.idArticulo,
      ARTICULO: row.articulo,
      SERVIDOR: row.server,
      SUCURSAL: row.sucursal,
      "U/M": row.unidadMedida,
      "INICIAL": toMetricValue(row.inventarioInicial, row.valorUnitario),
      COMPRA: toMetricValue(row.compras, row.valorUnitario),
      "REM (+)": toMetricValue(row.remisionPositiva, row.valorUnitario),
      "REM (-)": toMetricValue(row.remisionNegativa, row.valorUnitario),
      VENTA: toMetricValue(row.ventas, row.valorUnitario),
      MERMA: toMetricValue(row.ajusteNegativo, row.valorUnitario),
      TEORICO: toMetricValue(row.teorico, row.valorUnitario),
      "FINAL": toMetricValue(row.inventarioFinal, row.valorUnitario),
      DIFERENCIA: toMetricValue(row.diferencia, row.valorUnitario),
      MODO: metricMode === "cantidad" ? "Cantidades" : "Montos",
    }));
    addSheet("PorArticulo", rows);
  };

  const addFamiliaLineaSheet = () => {
    const rows = familyLineResults.map((row) => ({
      FAMILIA: row.familia,
      "LÍNEA": row.linea,
      "INICIAL": row.inventarioInicial,
      COMPRA: row.compras,
      "REM (+)": row.remisionPositiva,
      "REM (-)": row.remisionNegativa,
      VENTA: row.ventas,
      MERMA: row.ajusteNegativo,
      TEORICO: row.teorico,
      "FINAL": row.inventarioFinal,
      DIFERENCIA: row.diferencia,
    }));
    addSheet("FamiliaLinea", rows);
  };

  const addMovementSheets = () => {
    MOVEMENT_DATE_METRIC_OPTIONS.forEach((option) => {
      if (!movementDateAvailability[option.value]) {
        return;
      }
      const rows = buildMovementRowsForMetric(option.value);
      if (rows.length === 0) return;
      const sheetRows = rows.map((row) => {
        const base: Record<string, any> = {
          IDARTICULO: row.idArticulo,
          ARTICULO: row.articulo,
        };
        movementDateColumns.forEach((date) => {
          const current = row.porFecha[date];
          const qty = Number(current?.cantidad ?? 0);
          const amount = Number(current?.monto ?? 0);
          if (metricMode === "cantidad") {
            base[formatShortDayMonth(date)] = qty;
          } else if (metricMode === "monto") {
            base[formatShortDayMonth(date)] = amount;
          } else {
            base[`${formatShortDayMonth(date)} (Cant.)`] = qty;
            base[`${formatShortDayMonth(date)} (Monto)`] = amount;
          }
        });
        if (metricMode === "cantidad") {
          base.TOTAL = row.totalCantidad;
        } else if (metricMode === "monto") {
          base.TOTAL = row.totalMonto;
        } else {
          base["TOTAL (Cant.)"] = row.totalCantidad;
          base["TOTAL (Monto)"] = row.totalMonto;
        }
        return base;
      });
      addSheet(`Mov-${option.label}`, sheetRows);
    });
  };

  if (selectedTab === "all") {
    addArticuloSheet();
    addMovementSheets();
    if (hasAmountMode) addFamiliaLineaSheet();
  } else if (selectedTab === "movimientos-por-fecha") {
    addMovementSheets();
  } else if (selectedTab === "stock-por-familia-linea") {
    addFamiliaLineaSheet();
  } else {
    addArticuloSheet();
  }

  if (workbook.SheetNames.length === 0) {
    return;
  }

  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const suffix = exportMode === "cantidad" ? "cantidades" : "montos";
  XLSX.writeFile(workbook, `control-stock-${suffix}-${timestamp}.xlsx`);
};

export const exportControlStockPdf = (
  config: PdfExportConfig,
  tab?: ExportTab,
) => {
  const {
    filteredData,
    displayData,
    familyLineResults,
    movementDateAvailability,
    movementDateColumns,
    buildMovementRowsForMetric,
    metricMode,
    activeStockTab,
    hasAmountMode,
    pdfHiddenMetrics,
    pdfHideCosto,
    logoDataUrl,
    serverFilter,
    branchFilter,
    controlMode,
    differenceFilter,
    dateSelection,
    familyFilter,
    lineFilter,
    familyLabelMap,
    lineLabelMap,
    columnHasData,
    previousDateLabel,
    finalDateLabel,
    movementDateLabel,
  } = config;

  if (filteredData.length === 0) {
    return;
  }

  const selectedTab =
    tab ??
    (activeStockTab === "movimientos-por-fecha"
      ? "movimientos-por-fecha"
      : activeStockTab === "stock-por-familia-linea"
        ? "stock-por-familia-linea"
        : activeStockTab === "resumen"
          ? "stock-por-articulo"
        : activeStockTab === "stock-por-sucursal"
          ? "stock-por-articulo"
          : "stock-por-articulo");
  const exportMode = metricMode === "ambos" ? "monto" : metricMode;
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });
  const fechaHora = new Date().toLocaleString("es-PY");
  const usuarioActual = resolveUsuarioActual();

  const logoWidth = 110;
  const logoHeight = 24;
  const headerTopY = 24;
  const labelServidor = serverFilter || "Sin seleccionar";
  const labelSucursal =
    branchFilter.length === 0
      ? "Todas"
      : branchFilter.map((parte) => parte.trim()).filter(Boolean).join(" / ") ||
        "Sin seleccionar";
  const labelControl = controlMode === "inventario" ? "Inventario" : "Recetas";
  const labelDiferencias =
    differenceFilter === "con"
      ? "Con diferencias"
      : differenceFilter === "sin"
        ? "Sin diferencias"
        : differenceFilter === "positivas"
          ? "Positivas"
          : differenceFilter === "negativas"
            ? "Negativas"
            : "Todas";
  const labelModoVista =
    metricMode === "cantidad"
      ? "Cantidades"
      : metricMode === "monto"
        ? "Montos (PYG)"
        : "Cantidades + Montos";
  const labelPeriodo =
    dateSelection.mode === "week"
      ? `Semana ${dateSelection.week}`
      : dateSelection.mode === "month"
        ? `Mes ${dateSelection.month}`
        : dateSelection.mode === "range"
          ? `Rango ${dateSelection.rangeFrom} - ${dateSelection.rangeTo}`
          : `Día ${dateSelection.day}`;
  const labelFamilia =
    familyFilter === "todas" ? "Todas" : familyLabelMap[familyFilter] || familyFilter;
  const labelLinea = lineFilter === "todas" ? "Todas" : lineLabelMap[lineFilter] || lineFilter;

  const leftX = 40;
  const rightX = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const tableMarginLeft = leftX;
  const tableMarginRight = rightX;
  const labelColor: [number, number, number] = [0, 0, 0];
  const valueColor: [number, number, number] = [120, 120, 120];
  const titleX = leftX;
  const titleY = logoDataUrl ? headerTopY + logoHeight + 10 : headerTopY + 16;
  const titleBottomY = titleY + 35;
  const infoRightX = pageWidth - rightX;
  const infoMaxValueWidth = 160;
  const infoStartY = headerTopY + 4;
  const infoRowHeight = 11;
  const infoLines: Array<{ label: string; value: string }> = [
    { label: "Periodo", value: labelPeriodo },
    { label: "Sucursal", value: labelSucursal },
    { label: "Vista", value: labelModoVista },
    { label: "Control", value: labelControl },
    { label: "Diferencias", value: labelDiferencias },
    { label: "Familia", value: labelFamilia },
    { label: "Linea", value: labelLinea },
  ];

  const infoBottomY = infoStartY + (infoLines.length - 1) * infoRowHeight + 2;
  const headerContentBottom = Math.max(titleBottomY, infoBottomY);
  const headerHeight = headerContentBottom + 10;

  const fitTextToWidth = (text: string, maxWidth: number) => {
    if (maxWidth <= 0) return "";
    const raw = String(text ?? "");
    if (doc.getTextWidth(raw) <= maxWidth) {
      return raw;
    }
    let value = raw;
    while (value.length > 0 && doc.getTextWidth(`${value}...`) > maxWidth) {
      value = value.slice(0, -1);
    }
    return value ? `${value}...` : "...";
  };

  const renderInfoLine = (y: number, label: string, value: string) => {
    doc.setFontSize(8);
    const labelText = `${label}: `;
    const valueText = fitTextToWidth(value, infoMaxValueWidth);
    const labelWidth = doc.getTextWidth(labelText);
    const valueWidth = doc.getTextWidth(valueText);
    const startX = infoRightX - (labelWidth + valueWidth);
    doc.setTextColor(...labelColor);
    doc.text(labelText, startX, y);
    doc.setTextColor(...valueColor);
    doc.text(valueText, startX + labelWidth, y);
  };

  const renderHeader = () => {
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, "PNG", leftX, headerTopY - 4, logoWidth, logoHeight);
    }
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.text("Control de Stock", titleX, titleY);
    doc.setFontSize(8);
    doc.setTextColor(...labelColor);
    doc.text("Generado: ", titleX, titleY + 13);
    doc.setTextColor(...valueColor);
    doc.text(fechaHora, titleX + doc.getTextWidth("Generado: "), titleY + 13);
    doc.setTextColor(...labelColor);
    doc.text("Usuario: ", titleX, titleY + 24);
    doc.setTextColor(...valueColor);
    doc.text(usuarioActual, titleX + doc.getTextWidth("Usuario: "), titleY + 24);
    doc.setTextColor(...labelColor);
    doc.text("Servidor: ", titleX, titleY + 35);
    doc.setTextColor(...valueColor);
    doc.text(labelServidor, titleX + doc.getTextWidth("Servidor: "), titleY + 35);

    infoLines.forEach((item, index) => {
      const baseY = infoStartY + index * infoRowHeight;
      renderInfoLine(baseY, item.label, item.value);
    });
    doc.setTextColor(0, 0, 0);
  };

  const buildMetricText = (
    row: ControlStockRow,
    field: keyof ControlStockRow,
    signed = false,
  ) => {
    const qtyValue = Number(row[field] ?? 0);
    const amountValue = row.__isTotal && row.__amountTotals
      ? Number(row.__amountTotals[field] ?? 0)
      : qtyValue * Number(row.valorUnitario ?? 0);
    const qtyText = signed
      ? qtyValue > 0
        ? `+${formatNumber(qtyValue)}`
        : formatNumber(qtyValue)
      : formatNumber(qtyValue);
    const amountText = signed
      ? amountValue > 0
        ? `+${formatCurrency(amountValue)}`
        : formatCurrency(amountValue)
      : formatCurrency(amountValue);
    if (metricMode === "cantidad") {
      return qtyValue === 0 ? "—" : qtyText;
    }
    if (metricMode === "monto") {
      return amountValue === 0 ? "—" : amountText;
    }
    const bothEmpty = qtyValue === 0 && amountValue === 0;
    return bothEmpty ? "—" : `${qtyText}\n${amountText}`;
  };

  const buildTableForTab = (
    selected: "stock-por-articulo" | "movimientos-por-fecha" | "stock-por-familia-linea",
    movementMetric?: MovementDateMetric,
    movementLabel?: string,
  ) => {
    if (selected === "movimientos-por-fecha") {
      const rows = buildMovementRowsForMetric(movementMetric ?? "compras");
      if (rows.length === 0) {
        return null;
      }
      const head = [
        [
          "IDARTICULO",
          "ARTICULO",
          ...movementDateColumns.map((date) => formatShortDayMonth(date)),
          "TOTAL",
        ],
      ];
      const body = rows.map((row) => {
        const rowCells = [
          row.idArticulo,
          row.articulo,
          ...movementDateColumns.map((date) => {
            const current = row.porFecha[date];
            const qty = Number(current?.cantidad ?? 0);
            const amount = Number(current?.monto ?? 0);
            if (metricMode === "cantidad") {
              return qty === 0 ? "—" : formatNumber(qty);
            }
            if (metricMode === "monto") {
              return amount === 0 ? "—" : formatCurrency(amount);
            }
            return qty === 0 && amount === 0
              ? "—"
              : `${formatNumber(qty)}\n${formatCurrency(amount)}`;
          }),
        ];
        if (metricMode === "cantidad") {
          rowCells.push(row.totalCantidad === 0 ? "—" : formatNumber(row.totalCantidad));
        } else if (metricMode === "monto") {
          rowCells.push(row.totalMonto === 0 ? "—" : formatCurrency(row.totalMonto));
        } else {
          rowCells.push(
            row.totalCantidad === 0 && row.totalMonto === 0
              ? "—"
              : `${formatNumber(row.totalCantidad)}\n${formatCurrency(row.totalMonto)}`,
          );
        }
        return rowCells;
      });
      const columnStyles: Record<number, Partial<Styles>> = {
        0: { halign: "left" as HAlignType },
        1: { halign: "left" as HAlignType },
      };
      for (let i = 2; i < head[0].length; i += 1) {
        columnStyles[i] = { halign: "right" as HAlignType };
      }
      const title = movementLabel
        ? `Movimientos por fecha - ${movementLabel}`
        : "Movimientos por fecha";
      return { title, head, body, columnStyles };
    }

    if (selected === "stock-por-familia-linea") {
      const head = [
        [
          "FAMILIA",
          "LÍNEA",
          "INICIAL",
          "COMPRA",
          "REM(+)",
          "REM(-)",
          "VENTA",
          "MERMA",
          "TEORICO",
          "FINAL",
          "DIF.",
        ],
      ];
      const body: any[] = familyLineResults.map((row) => [
        row.familia,
        row.linea,
        formatCurrency(row.inventarioInicial),
        formatCurrency(row.compras),
        formatCurrency(row.remisionPositiva),
        formatCurrency(row.remisionNegativa),
        formatCurrency(row.ventas),
        formatCurrency(row.ajusteNegativo),
        formatCurrency(row.teorico),
        formatCurrency(row.inventarioFinal),
        formatCurrency(row.diferencia),
      ]);
      if (familyLineResults.length > 0) {
        const totals = familyLineResults.reduce(
          (acc, row) => {
            acc.inventarioInicial += Number(row.inventarioInicial ?? 0);
            acc.compras += Number(row.compras ?? 0);
            acc.remisionPositiva += Number(row.remisionPositiva ?? 0);
            acc.remisionNegativa += Number(row.remisionNegativa ?? 0);
            acc.ventas += Number(row.ventas ?? 0);
            acc.ajusteNegativo += Number(row.ajusteNegativo ?? 0);
            acc.teorico += Number(row.teorico ?? 0);
            acc.inventarioFinal += Number(row.inventarioFinal ?? 0);
            acc.diferencia += Number(row.diferencia ?? 0);
            return acc;
          },
          {
            inventarioInicial: 0,
            compras: 0,
            remisionPositiva: 0,
            remisionNegativa: 0,
            ventas: 0,
            ajusteNegativo: 0,
            teorico: 0,
            inventarioFinal: 0,
            diferencia: 0,
          },
        );
        body.push([
          {
            content: "TOTAL GENERAL",
            styles: { fontStyle: "bold", fillColor: [235, 244, 255] },
          },
          {
            content: "",
            styles: { fontStyle: "bold", fillColor: [235, 244, 255] },
          },
          {
            content: formatCurrency(totals.inventarioInicial),
            styles: { fontStyle: "bold", fillColor: [235, 244, 255] },
          },
          {
            content: formatCurrency(totals.compras),
            styles: { fontStyle: "bold", fillColor: [235, 244, 255] },
          },
          {
            content: formatCurrency(totals.remisionPositiva),
            styles: { fontStyle: "bold", fillColor: [235, 244, 255] },
          },
          {
            content: formatCurrency(totals.remisionNegativa),
            styles: { fontStyle: "bold", fillColor: [235, 244, 255] },
          },
          {
            content: formatCurrency(totals.ventas),
            styles: { fontStyle: "bold", fillColor: [235, 244, 255] },
          },
          {
            content: formatCurrency(totals.ajusteNegativo),
            styles: { fontStyle: "bold", fillColor: [235, 244, 255] },
          },
          {
            content: formatCurrency(totals.teorico),
            styles: { fontStyle: "bold", fillColor: [235, 244, 255] },
          },
          {
            content: formatCurrency(totals.inventarioFinal),
            styles: { fontStyle: "bold", fillColor: [235, 244, 255] },
          },
          {
            content: formatCurrency(totals.diferencia),
            styles: { fontStyle: "bold", fillColor: [235, 244, 255] },
          },
        ]);
      }
      const columnStyles: Record<number, Partial<Styles>> = {
        0: { halign: "left" as HAlignType },
        1: { halign: "left" as HAlignType },
        2: { halign: "right" as HAlignType },
        3: { halign: "right" as HAlignType },
        4: { halign: "right" as HAlignType },
        5: { halign: "right" as HAlignType },
        6: { halign: "right" as HAlignType },
        7: { halign: "right" as HAlignType },
        8: { halign: "right" as HAlignType },
        9: { halign: "right" as HAlignType },
        10: { halign: "right" as HAlignType },
      };
      return { title: "Resultado por familia y línea", head, body, columnStyles };
    }

    const metricColumns: Array<{
      key: keyof PdfHiddenMetrics;
      label: string;
      sub: string;
      value: (row: ControlStockRow) => string;
    }> = [
      {
        key: "inventarioInicial",
        label: "INICIAL",
        sub: columnHasData.inventarioInicial ? previousDateLabel || "" : "",
        value: (row: ControlStockRow) => buildMetricText(row, "inventarioInicial"),
      },
      {
        key: "compras",
        label: "COMPRA",
        sub: columnHasData.compras ? movementDateLabel || "" : "",
        value: (row: ControlStockRow) => buildMetricText(row, "compras"),
      },
      {
        key: "remisionPositiva",
        label: "REM(+)",
        sub: columnHasData.remisionPositiva ? movementDateLabel || "" : "",
        value: (row: ControlStockRow) => buildMetricText(row, "remisionPositiva"),
      },
      {
        key: "remisionNegativa",
        label: "REM(-)",
        sub: columnHasData.remisionNegativa ? movementDateLabel || "" : "",
        value: (row: ControlStockRow) => buildMetricText(row, "remisionNegativa"),
      },
      {
        key: "ventas",
        label: "VENTA",
        sub: columnHasData.ventas ? movementDateLabel || "" : "",
        value: (row: ControlStockRow) => buildMetricText(row, "ventas"),
      },
      {
        key: "ajusteNegativo",
        label: "MERMA",
        sub: columnHasData.ajusteNegativo ? movementDateLabel || "" : "",
        value: (row: ControlStockRow) => buildMetricText(row, "ajusteNegativo"),
      },
      {
        key: "teorico",
        label: "TEORICO",
        sub: "",
        value: (row: ControlStockRow) => buildMetricText(row, "teorico"),
      },
      {
        key: "inventarioFinal",
        label: "FINAL",
        sub: columnHasData.inventarioFinal ? finalDateLabel || "" : "",
        value: (row: ControlStockRow) => buildMetricText(row, "inventarioFinal"),
      },
      {
        key: "diferencia",
        label: "DIF.",
        sub: "",
        value: (row: ControlStockRow) => buildMetricText(row, "diferencia", true),
      },
    ];

    const visibleMetricColumns = metricColumns.filter(
      (col) => !pdfHiddenMetrics[col.key],
    );

    const head = (() => {
      const baseHead = [
        "IDARTICULO",
        "ARTICULO",
        "COSTO MP U.",
        "U/M",
        ...visibleMetricColumns.map((col) => col.label),
      ];
      const baseSubHead = [
        "",
        "",
        "",
        "",
        ...visibleMetricColumns.map((col) => col.sub),
      ];
      if (!pdfHideCosto) {
        return [baseHead, baseSubHead];
      }
      const withoutCosto = (row: string[]) =>
        row.filter((_, index) => index !== 2);
      return [withoutCosto(baseHead), withoutCosto(baseSubHead)];
    })();

    const tableData = (() => {
      const result: any[] = [];
      const colCount = head[0].length;
      let grandTotalRow: ControlStockRow | null = null;

      const buildDetailRow = (row: ControlStockRow) => {
        const baseRow = [
          row.idArticulo,
          row.articulo,
          formatCurrency(Number(row.valorUnitario ?? 0)),
          row.unidadMedida,
          ...visibleMetricColumns.map((col) => col.value(row)),
        ];
        if (pdfHideCosto) {
          baseRow.splice(2, 1);
        }
        return baseRow;
      };

      const buildTotalRow = (row: ControlStockRow) => {
        const label = row.__totalLabel ?? (row.__isGrandTotal ? "Total general" : "Subtotal");
        const baseRow = [
          label,
          row.__isGrandTotal ? "Totales (PYG)" : "",
          "-",
          "",
          ...visibleMetricColumns.map((col) => col.value(row)),
        ];
        if (pdfHideCosto) {
          baseRow.splice(2, 1);
        }
        const isGrandTotal = Boolean(row.__isGrandTotal);
        const fillColor: [number, number, number] = isGrandTotal
          ? [33, 37, 41]
          : [245, 245, 245];
        const textColor: [number, number, number] = isGrandTotal
          ? [255, 255, 255]
          : [33, 37, 41];
        return baseRow.map((cell) => ({
          content: String(cell ?? ""),
          styles: { fontStyle: "bold", fillColor, textColor },
        }));
      };

      const sourceRows = displayData.length > 0 ? displayData : filteredData;
      sourceRows.forEach((row) => {
        if (row.__isCollectionHeader) {
          const label = row.__collectionLabel ?? "Sin colección";
          const diffValue = Number(row.__collectionDiff ?? 0);
          const diffText =
            diffValue > 0 ? `+${formatNumber(diffValue)}` : formatNumber(diffValue);
          const diffColor: [number, number, number] =
            diffValue > 0 ? [22, 163, 74] : diffValue < 0 ? [220, 53, 69] : [108, 117, 125];
          result.push([
            {
              content: `Colección: ${label}`,
              colSpan: Math.max(1, colCount - 2),
              styles: {
                fontStyle: "bold",
                fillColor: [245, 245, 245],
                textColor: [90, 90, 90],
                halign: "left",
              },
            },
            {
              content: `DIF: ${diffText}`,
              colSpan: 2,
              styles: {
                fontStyle: "bold",
                fillColor: [245, 245, 245],
                textColor: diffColor,
                halign: "right",
              },
            },
          ]);
          return;
        }
        if (row.__isTotal) {
          if (row.__isGrandTotal) {
            grandTotalRow = row;
            return;
          }
          result.push(buildTotalRow(row));
          return;
        }
        result.push(buildDetailRow(row));
      });

      if (!grandTotalRow && filteredData.length > 0) {
        const totalsQty = {
          inventarioInicial: 0,
          compras: 0,
          remisionPositiva: 0,
          remisionNegativa: 0,
          ventas: 0,
          ajusteNegativo: 0,
          teorico: 0,
          inventarioFinal: 0,
          diferencia: 0,
        };
        const totalsAmount = {
          inventarioInicial: 0,
          compras: 0,
          remisionPositiva: 0,
          remisionNegativa: 0,
          ventas: 0,
          ajusteNegativo: 0,
          teorico: 0,
          inventarioFinal: 0,
          diferencia: 0,
        };
        filteredData.forEach((row) => {
          const costo = Number(row.valorUnitario ?? 0);
          totalsQty.inventarioInicial += Number(row.inventarioInicial ?? 0);
          totalsQty.compras += Number(row.compras ?? 0);
          totalsQty.remisionPositiva += Number(row.remisionPositiva ?? 0);
          totalsQty.remisionNegativa += Number(row.remisionNegativa ?? 0);
          totalsQty.ventas += Number(row.ventas ?? 0);
          totalsQty.ajusteNegativo += Number(row.ajusteNegativo ?? 0);
          totalsQty.teorico += Number(row.teorico ?? 0);
          totalsQty.inventarioFinal += Number(row.inventarioFinal ?? 0);
          totalsQty.diferencia += Number(row.diferencia ?? 0);
          totalsAmount.inventarioInicial += Number(row.inventarioInicial ?? 0) * costo;
          totalsAmount.compras += Number(row.compras ?? 0) * costo;
          totalsAmount.remisionPositiva += Number(row.remisionPositiva ?? 0) * costo;
          totalsAmount.remisionNegativa += Number(row.remisionNegativa ?? 0) * costo;
          totalsAmount.ventas += Number(row.ventas ?? 0) * costo;
          totalsAmount.ajusteNegativo += Number(row.ajusteNegativo ?? 0) * costo;
          totalsAmount.teorico += Number(row.teorico ?? 0) * costo;
          totalsAmount.inventarioFinal += Number(row.inventarioFinal ?? 0) * costo;
          totalsAmount.diferencia += Number(row.diferencia ?? 0) * costo;
        });
        grandTotalRow = {
          idArticulo: "TOTAL",
          articulo: "",
          server: "",
          sucursal: "",
          fechaReferencia: "",
          valorUnitario: 1,
          unidadMedida: "",
          familia: null,
          linea: null,
          inventarioInicial: totalsQty.inventarioInicial,
          compras: totalsQty.compras,
          remisionPositiva: totalsQty.remisionPositiva,
          remisionNegativa: totalsQty.remisionNegativa,
          ventas: totalsQty.ventas,
          ajustePositivo: 0,
          ajusteNegativo: totalsQty.ajusteNegativo,
          teorico: totalsQty.teorico,
          inventarioFinal: totalsQty.inventarioFinal,
          diferencia: totalsQty.diferencia,
          __isTotal: true,
          __isGrandTotal: true,
          __totalLabel: "Total general",
          __amountTotals: totalsAmount,
          __familiaId: null,
          __lineaId: null,
        };
      }

      return {
        bodyRows: grandTotalRow ? [...result, buildTotalRow(grandTotalRow)] : result,
      };
    })();
    const body = tableData.bodyRows;

    const columnDefs = [
      { align: "left" as HAlignType },
      { align: "left" as HAlignType },
      { align: "right" as HAlignType },
      { align: "left" as HAlignType },
      ...visibleMetricColumns.map(() => ({ align: "right" as HAlignType })),
    ];
    if (pdfHideCosto) {
      columnDefs.splice(2, 1);
    }
    const columnStyles: Record<number, Partial<Styles>> = {};
    columnDefs.forEach((def, index) => {
      columnStyles[index] = { halign: def.align };
    });

    return {
      title: "Control de stock por artículo",
      head,
      body,
      columnStyles,
    };
  };

  const tableSpecs: Array<{
    title: string;
    head: string[][];
    body: any[];
    columnStyles: Record<number, Partial<Styles>>;
  }> = [];

  const addMovementSpecs = () => {
    MOVEMENT_DATE_METRIC_OPTIONS.forEach((option) => {
      if (!movementDateAvailability[option.value]) {
        return;
      }
      const spec = buildTableForTab(
        "movimientos-por-fecha",
        option.value,
        option.label,
      );
      if (spec) {
        tableSpecs.push(spec);
      }
    });
  };

  if (selectedTab === "all") {
    const specArticulo = buildTableForTab("stock-por-articulo");
    if (specArticulo) tableSpecs.push(specArticulo);
    addMovementSpecs();
    if (hasAmountMode) {
      const specFamilia = buildTableForTab("stock-por-familia-linea");
      if (specFamilia) tableSpecs.push(specFamilia);
    }
  } else if (selectedTab === "movimientos-por-fecha") {
    addMovementSpecs();
  } else {
    const spec = buildTableForTab(selectedTab);
    if (spec) tableSpecs.push(spec);
  }

  tableSpecs.forEach((spec, index) => {
    if (index > 0) {
      doc.addPage();
    }
    const titleY = headerHeight + 10;
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(spec.title, tableMarginLeft, titleY);
    autoTable(doc, {
      head: spec.head,
      body: spec.body,
      startY: titleY + 6,
      margin: { top: headerHeight, left: tableMarginLeft, right: tableMarginRight },
      styles: { fontSize: 7, cellPadding: 3 },
      headStyles: { fillColor: [33, 37, 41] },
      theme: "grid",
      columnStyles: spec.columnStyles,
      didParseCell: (data) => {
        if (data.section === "head" && data.row.index === 1) {
          data.cell.styles.fontSize = 6;
          data.cell.styles.textColor = 200;
        }
      },
      didDrawPage: () => {
        renderHeader();
      },
    });
  });

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.text(
      `Página ${page} de ${totalPages}`,
      pageWidth - 30,
      pageHeight - 20,
      { align: "right" },
    );
  }

  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const suffix = exportMode === "cantidad" ? "cantidades" : "montos";
  doc.save(`control-stock-${suffix}-${timestamp}.pdf`);
};
